// SDEV-FILE
// Copyright (c) 2026 SDEV. All rights reserved.
// Workspace: clientside
// File: navigation.fallback.js
// Created: 2026-03-13T17:25:46.785Z

/*
BEDROCK Core
Copyright (c) 2026 Patrick Schimmelpfennig

All rights reserved.

Unauthorized copying, modification, distribution or use of this software,
in whole or in part, is strictly prohibited without explicit written permission
from the author or an authorized BEDROCK license grant.
*/

/*
────────────────────────────────────────
BEDROCK MODEL ABSTRACTION
────────────────────────────────────────

In Bedrock every system object is modeled as an Entity.

Core Entities:

Identity   → actors (user, device, service)
Entity     → objects in the system
Relation   → connections between entities
Event      → actions or state changes
Lifecycle  → state machine of entities
Command    → executable operations

Navigation in Bedrock is NOT a special system.

It is modeled as:

Entity (navigation_item)
    └ relation → belongs_to
           ↓
Entity (navigation_category)

Example:

NavigationItem: "identity"
    entity_type: navigation_item

NavigationCategory: "core"
    entity_type: navigation_category


Fallback Navigation
───────────────────

The fallback navigation is a simplified representation
of this model used only to bootstrap the UI if the
database navigation cannot be loaded.

It intentionally avoids:

- relations
- meta structures
- audit information
- lifecycle state

to guarantee system startup.
*/
export const navigationFallbackData = {

    categories: [
        {
            id: "core",
            entity_key: "core",
            entity_type: "navigation_category",
            label: "Core",
            sort_order: 10,
            active: true
        },
        {
            id: "discovery",
            entity_key: "discovery",
            entity_type: "navigation_category",
            label: "Discovery",
            sort_order: 20,
            active: true
        },
        {
            id: "control-plane",
            entity_key: "control-plane",
            entity_type: "navigation_category",
            label: "Control Plane",
            sort_order: 5,
            active: true
        }
    ],

    items: [

        {
            id: "instance",
            entity_key: "instance",
            entity_type: "navigation_item",
            category_key: "control-plane",
            route: "/instances",
            component: "InstanceView",
            label: "Instanzen",
            sort_order: 10,
            visible: true,
            active: true,
            meta: {
                subtitle: "Verwaltete Kundeninstanzen, Machine Trust und freigegebene Bedrock-Projektionen",
                group: "Control Plane",
                actions: [],
                tags: { entity: "managed.instance" }
            }
        },

        {
            id: "identity",
            entity_key: "identity",
            entity_type: "navigation_item",

            category_key: "core",

            route: "/identity",
            component: "IdentityView",

            label: "Identity",
            sort_order: 10,

            visible: true,
            active: true,
            meta: {
                subtitle: "Actors in the system",
                group: "Core",
                actions: [
                    { label: "Create", action: "create", variant: "primary" }
                ],
                tags: {
                    entity: "identity"
                }
            }
        },

        {
            id: "entity",
            entity_key: "entity",
            entity_type: "navigation_item",

            category_key: "core",

            route: "/entity",
            component: "EntityView",

            label: "Entity",
            sort_order: 20,

            visible: true,
            active: true,
            meta: {
                subtitle: "Objects in the system",
                group: "Core",
                actions: [
                    { label: "Create", action: "create", variant: "primary" }
                ],
                tags: {
                    entity: "entity"
                }
            }
        },

        {
            id: "relation",
            entity_key: "relation",
            entity_type: "navigation_item",

            category_key: "core",

            route: "/relation",
            component: "RelationView",

            label: "Relation",
            sort_order: 30,

            visible: true,
            active: true,
            meta: {
                subtitle: "Relations in the system",
                group: "Core",
                actions: [
                    { label: "Create", action: "create", variant: "primary" }
                ],
                tags: {
                    entity: "relation"
                }
            }
        },

        {
            id: "event",
            entity_key: "event",
            entity_type: "navigation_item",

            category_key: "core",

            route: "/event",
            component: "EventView",

            label: "Event",
            sort_order: 40,

            visible: true,
            active: true,
            meta: {
                subtitle: "Events in the system",
                group: "Core",
                actions: [
                    { label: "Create", action: "create", variant: "primary" }
                ],
                tags: {
                    entity: "event"
                }
            }
        },

        {
            id: "lifecycle",
            entity_key: "lifecycle",
            entity_type: "navigation_item",

            category_key: "core",

            route: "/lifecycle",
            component: "LifecycleView",

            label: "Lifecycle",
            sort_order: 50,

            visible: true,
            active: true,
            meta: {
                subtitle: "Lifecycles in the system",
                group: "Core",
                actions: [
                    { label: "Create", action: "create", variant: "primary" }
                ],
                tags: {
                    entity: "lifecycle"
                }
            }
        },

        {
            id: "command",
            entity_key: "command",
            entity_type: "navigation_item",

            category_key: "core",

            route: "/command",
            component: "CommandView",

            label: "Command",
            sort_order: 60,

            visible: true,
            active: true,
            meta: {
                subtitle: "Commands in the system",
                group: "Core",
                actions: [
                    { label: "Create", action: "create", variant: "primary" }
                ],
                tags: {
                    entity: "command"
                }
            }
        },
        {
            id: "discover",
            entity_key: "discover",
            entity_type: "navigation_item",

            category_key: "discovery",

            route: "/discovery",
            component: "DiscoveryView",

            label: "Discovery",
            sort_order: 20,

            visible: true,
            active: true,

            meta: {
                subtitle: "Discover the world and more",
                group: "Core",
                actions: [],
                tags: {
                    entity: "discover"
                }
            }
        },
        {
            id: "wizard",
            entity_key: "wizard",
            entity_type: "navigation_item",

            category_key: "discovery",

            route: "/wizard",
            component: "WizardView",

            label: "Wizard",
            sort_order: 10,

            visible: true,
            active: true,

            meta: {
                subtitle: "System provisioning wizard",
                group: "Core",
                actions: [],
                tags: {
                    entity: "wizard"
                }
            }
        }

    ]
}
